import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Box,
  Chip,
  Menu,
  MenuItem,
} from "@mui/material";
import { ContentCopy, MoreVert, DeleteForever } from "@mui/icons-material";
import dayjs from "dayjs";
import { useState } from "react";

const UserTable = ({
  users,
  sortBy,
  sortOrder,
  onSort,
  onCopyUserId,
  onMessageUser,
  onViewTasks,
  onDeleteUser,
  onToggleFriend,
  getCompletionRate,
  getActivityStatus,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState(null);
  const [selectedUserIsFriend, setSelectedUserIsFriend] = useState(false);

  const handleMenuOpen = (event, userId, userName, isFriend) => {
    setAnchorEl(event.currentTarget);
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setSelectedUserIsFriend(isFriend);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUserId(null);
    setSelectedUserName(null);
    setSelectedUserIsFriend(false);
  };

  const handleMessageUser = () => {
    if (selectedUserId) {
      onMessageUser(selectedUserId);
    }
    handleMenuClose();
  };

  const handleViewTasks = () => {
    if (selectedUserId) {
      onViewTasks(selectedUserId);
    }
    handleMenuClose();
  };

  const handleDeleteUser = () => {
    if (selectedUserId && onDeleteUser) {
      onDeleteUser(selectedUserId, selectedUserName);
    }
    handleMenuClose();
  };

  const handleToggleFriend = () => {
    if (selectedUserId && onToggleFriend) {
      onToggleFriend(selectedUserId);
    }
    handleMenuClose();
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>User ID</TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === "name"}
                direction={sortBy === "name" ? sortOrder : "asc"}
                onClick={() => onSort("name")}
              >
                User
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === "createdAt"}
                direction={sortBy === "createdAt" ? sortOrder : "asc"}
                onClick={() => onSort("createdAt")}
              >
                Signed Up
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === "lastVisited"}
                direction={sortBy === "lastVisited" ? sortOrder : "asc"}
                onClick={() => onSort("lastVisited")}
              >
                Last Active
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === "totalVisits"}
                direction={sortBy === "totalVisits" ? sortOrder : "asc"}
                onClick={() => onSort("totalVisits")}
              >
                Visits
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === "tasksCreated"}
                direction={sortBy === "tasksCreated" ? sortOrder : "asc"}
                onClick={() => onSort("tasksCreated")}
              >
                Tasks Created
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === "tasksCompleted"}
                direction={sortBy === "tasksCompleted" ? sortOrder : "asc"}
                onClick={() => onSort("tasksCompleted")}
              >
                Completion Rate
              </TableSortLabel>
            </TableCell>
            <TableCell>Subscription</TableCell>
            <TableCell>Activity</TableCell>
            <TableCell align="right">Options</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => {
            const {
              userId,
              name,
              email,
              tasksCreated,
              tasksCompleted,
              lastVisited,
              totalVisits,
              createdAt,
              subscription,
              friend,
            } = user;

            const completionRate = getCompletionRate(
              tasksCreated,
              tasksCompleted,
            );
            const activityStatus = getActivityStatus(lastVisited);

            return (
              <TableRow key={userId} hover>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        maxWidth: "120px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={userId}
                    >
                      {userId}
                    </Typography>
                    <Tooltip title="Copy User ID">
                      <IconButton
                        size="small"
                        onClick={() => onCopyUserId(userId)}
                        sx={{ minWidth: "auto", p: 0.5 }}
                      >
                        <ContentCopy sx={{ fontSize: "0.875rem" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {name || "No name"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {dayjs(createdAt).format("MMM DD, YYYY")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(createdAt).fromNow()}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {dayjs(lastVisited).format("MMM DD, YYYY")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(lastVisited).fromNow()}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{totalVisits || 0}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{tasksCreated || 0}</Typography>
                </TableCell>
                <TableCell>
                  <Box
                    sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}
                  >
                    <Typography variant="body2" sx={{ m: "0 !important" }}>
                      {isNaN(completionRate) ? "0%" : `${completionRate}%`}
                    </Typography>
                    {tasksCompleted > 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ m: "0 !important" }}
                      >
                        ({tasksCompleted} completed)
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {(() => {
                    const subscriptionStatus = subscription?.subscriptionStatus;
                    const isSubscribed =
                      subscriptionStatus === "active" ||
                      subscriptionStatus === "cancelling";
                    const isFriend = friend === true;

                    if (isFriend) {
                      return (
                        <Chip
                          label="Friend"
                          size="small"
                          sx={{
                            backgroundColor: "#813773",
                            color: "#FFFFFF",
                            fontWeight: 500,
                            "& .MuiChip-label": {
                              color: "#FFFFFF",
                            },
                          }}
                        />
                      );
                    } else if (isSubscribed) {
                      return (
                        <Chip
                          label="Subscribed"
                          size="small"
                          sx={{
                            backgroundColor: "#197B68",
                            color: "#FFFFFF",
                            fontWeight: 500,
                            "& .MuiChip-label": {
                              color: "#FFFFFF",
                            },
                          }}
                        />
                      );
                    } else {
                      return (
                        <Chip
                          label="Unsubscribed"
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: "primary.main",
                            color: "primary.main",
                            fontWeight: 500,
                          }}
                        />
                      );
                    }
                  })()}
                </TableCell>
                <TableCell>
                  <Chip
                    label={activityStatus.label}
                    color={activityStatus.color}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, userId, name, friend)}
                  >
                    <MoreVert sx={{ fontSize: "1.25rem" }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
          {users.length === 0 && (
            <TableRow>
              <TableCell colSpan={10} align="center">
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No users found.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewTasks}>View Tasks</MenuItem>
        <MenuItem onClick={handleMessageUser}>Message User</MenuItem>
        <MenuItem onClick={handleToggleFriend}>
          {selectedUserIsFriend ? "Remove Friend" : "Make Friend"}
        </MenuItem>
        <MenuItem onClick={handleDeleteUser} sx={{ color: "error.main" }}>
          <DeleteForever sx={{ fontSize: "1.25rem", mr: 1 }} />
          Delete User
        </MenuItem>
      </Menu>
    </TableContainer>
  );
};

export default UserTable;
